import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { ScrollView, YStack } from "tamagui";
import { z } from "zod";

import { useStartIdVerification } from "@/api/generated";
import { useUpdateUserProfile } from "@/api/mutations/";
import type { UserProfileFormData } from "@/api/mutations/userProfile";
import { useGetUserProfile } from "@/api/queries/";
import {
  Button,
  Footer,
  Header,
  Heading,
  Link,
  Paragraph,
  Select,
} from "@/components";
import { US_STATES } from "@/settings/constants";

const stateValidationSchema = z.object({
  residingState: z.string().min(1, "Please select a state"),
});

type StateValidationSchemaType = z.infer<typeof stateValidationSchema>;

interface StateValidationProps {
  onClose?: () => void;
  showConsentDisclaimer?: boolean;
  onContinue?: () => void;
}

const StateValidation = ({
  onClose,
  showConsentDisclaimer = false,
  onContinue,
}: StateValidationProps) => {
  const { data, isLoading: isUserProfileLoading } = useGetUserProfile();
  const { mutate: updateUserProfile, isPending: isUpdating } =
    useUpdateUserProfile();
  const { mutate: startIdVerification } = useStartIdVerification();

  const isDataLoading = isUpdating || isUserProfileLoading;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<StateValidationSchemaType>({
    resolver: zodResolver(stateValidationSchema),
    mode: "onChange",
    defaultValues: {
      residingState: data?.residingState || "",
    },
  });

  useEffect(() => {
    if (data?.residingState) {
      reset({ residingState: data.residingState }, { keepFieldsRef: true });
    }
  }, [data?.residingState, reset]);

  const handleContinue = handleSubmit((formData) => {
    // We try to start this process earlier, but if we reach this screen
    // and don't have idVerification, we need to start
    if (!data?.idVerification) {
      startIdVerification();
    }

    updateUserProfile(
      { data: formData as UserProfileFormData },
      {
        onSuccess: () => {
          onContinue?.();
        },
      },
    );
  });

  const stickyFooter = (
    <Footer
      padding="$200"
      gap="$100"
      $lg={{
        flexDirection: "row-reverse",
        alignItems: "center",
        gap: "$300",
      }}
    >
      <Button
        dataTrackingId="state-validation-continue-button"
        constrained
        mode="primary"
        onPress={handleContinue}
        loading={isUpdating}
        disabled={isDataLoading}
      >
        Continue
      </Button>
      {showConsentDisclaimer && (
        <Paragraph
          size="$xs"
          lineHeight="$md"
          color="$textSecondary"
          textAlign="center"
        >
          By continuing you agree to CHMG&apos;s{" "}
          <Link
            size="$xs"
            lineHeight="$md"
            external
            href="https://www.curaihealth.com/informed-consent"
            bold
          >
            Telehealth Informed Consent
          </Link>
          .
        </Paragraph>
      )}
    </Footer>
  );

  return (
    <>
      <ScrollView
        flex={1}
        contentContainerStyle={{
          flex: 1,
          paddingHorizontal: "$200",
          paddingBottom: "$400",
        }}
      >
        <YStack flex={1}>
          <Header gap="$100">
            <Heading size="$xl">What state are you currently in?</Heading>
            <Paragraph size="$sm" color="$textSecondary">
              We use this information to connect you to a licensed clinician in
              your state.
            </Paragraph>
          </Header>
          <YStack gap="$200" marginTop="$200">
            <Controller
              control={control}
              name="residingState"
              render={({ field: { onChange, value } }) => (
                <Select
                  label="Current state"
                  items={US_STATES}
                  value={value}
                  onValueChange={onChange}
                  errorText={errors.residingState?.message}
                />
              )}
            />
          </YStack>
        </YStack>
      </ScrollView>
      {stickyFooter}
    </>
  );
};

export default StateValidation;
